import {
  LOCAL_ADD_WORK_INSTRUCTION_PACKAGE,
  LOCAL_DELETE_WORK_INSTRUCTION_PACKAGE,
  LOCAL_ADD_WORK_INSTRUCTION,
  LOCAL_UPDATE_WORK_INSTRUCTION,
  LOCAL_DELETE_WORK_INSTRUCTION,
  LOCAL_ADD_WORK_INSTRUCTION_STEP,
  LOCAL_UPDATE_WORK_INSTRUCTION_STEP,
  LOCAL_DELETE_WORK_INSTRUCTION_STEP,
  LOCAL_SET_WORK_INSTRUCTION_PACKAGES,
  LOCAL_SET_WORK_INSTRUCTIONS_FOR_PACKAGE,
  LOCAL_SET_ALL_WORK_INSTRUCTIONS_BY_PACKAGE,
  LOCAL_SET_FILES_LINKED_TO_WORK_INSTRUCTION_STEP,
  LOCAL_SET_TASKS_TO_WORK_INSTRUCTION_STEP,
  LOCAL_SET_FILES_LINKED_TO_WORK_INSTRUCTION,
  LOCAL_SET_TASKS_TO_WORK_INSTRUCTION,
  LOCAL_SIGN_OFF_STEP,
  LOCAL_REOPEN_STEP,
  LOCAL_CHANGE_WORK_INSTRUCTION_STEPS_ORDER,
  LOCAL_CHANGE_WORK_INSTRUCTIONS_ORDER,
  LOCAL_SET_JOB_WORK_INSTRUCTION_STATUS,
} from '../mutations/mutationTypes';

import client from '../../../config/api';

import loadJobWorkInstructionStatus from '../../../helpers/workInstructions/loadJobWorkInstructionStatus';
import WorkInstructionPackageDTO from '../../../shared/dtos/WorkInstructionPackageDTO';
import WorkInstructionDTO from '../../../shared/dtos/WorkInstructionDTO';

export default {
  async UPDATE_WORK_INSTRUCTION_PACKAGE_FAVORITE_COUNT(_, payload) {
    await client.put(
      `/work-instructions-package/${payload.id}?preserveModified=1`,
      {
        favoriteCount: payload.favoriteCount,
      }
    );
  },

  async UPDATE_WORK_INSTRUCTION_PACKAGE(_, { packageId, payload }) {
    const response = await client.put(
      `/work-instructions-package/${packageId}`,
      payload
    );

    return response?.data;
  },

  async UPDATE_WORK_INSTRUCTION_PACKAGE_DOCUMENTS(
    _,
    { packageId, linkedDocuments }
  ) {
    const response = await client.put(
      `/work-instructions-package/${packageId}/documents`,
      {
        linkedDocuments,
      }
    );

    return response?.data;
  },

  async ADD_WORK_INSTRUCTION_PACKAGE({ commit }, payload) {
    try {
      const normalizedPayload = new WorkInstructionPackageDTO(payload);
      const response = await client.post(
        '/work-instructions-package',
        normalizedPayload
      );
      commit(
        LOCAL_ADD_WORK_INSTRUCTION_PACKAGE,
        new WorkInstructionPackageDTO(response.data)
      );
    } catch (error) {
      console.error('Failed to add work instruction package', error);
      throw error;
    }
  },

  FETCH_WORK_INSTRUCTION_PACKAGES: async ({ commit }, jobId) => {
    const response = await client.get(
      `/work-instructions-package/job/${jobId}`
    );

    const packages = (response.data || []).map(
      (wiPackage) => new WorkInstructionPackageDTO(wiPackage)
    );

    commit(LOCAL_SET_WORK_INSTRUCTION_PACKAGES, packages);
  },

  FETCH_ALL_WORK_INSTRUCTION_PACKAGES: async (_, options = {}) => {
    const params = {};

    if (options?.statusFilter && options.statusFilter.length > 0) {
      params.statusFilter = options.statusFilter;
    }

    const response = await client.get('/work-instructions-package', {
      params: Object.keys(params).length > 0 ? params : undefined,
    });

    return response?.data || [];
  },

  FETCH_STANDARD_WORK_INSTRUCTION_PACKAGES_PAGE_REQUEST: async (
    _,
    requestConfig
  ) => {
    return client.get('/work-instructions-package', requestConfig);
  },

  FETCH_STANDARD_WORK_INSTRUCTION_PACKAGES_PAGE: async (
    { dispatch },
    options = {}
  ) => {
    const params = {
      includeUsageStats: options?.includeUsageStats !== false,
      limit: Number(options?.limit) > 0 ? Number(options.limit) : 30,
      _requestTs: Date.now(),
    };

    if (options?.sortByModified) {
      params.sortByModified = true;
    }

    if (options?.deleted) {
      params.deleted = true;
    }

    if (options?.type) {
      params.type = options.type;
    }

    if (options?.statusFilter && options.statusFilter.length > 0) {
      params.statusFilter = options.statusFilter;
    }

    if (options?.cursor) {
      params.startKey = options.cursor;
    }

    const requestConfig = {
      params,
    };

    let response;
    try {
      response = await dispatch(
        'FETCH_STANDARD_WORK_INSTRUCTION_PACKAGES_PAGE_REQUEST',
        requestConfig
      );
    } catch (error) {
      const statusCode = Number(error?.response?.status || 0);

      if (statusCode === 304) {
        const retryParams = {
          ...params,
          _requestTs: Date.now(),
          _requestNonce: Math.random().toString(36).slice(2),
        };

        response = await dispatch(
          'FETCH_STANDARD_WORK_INSTRUCTION_PACKAGES_PAGE_REQUEST',
          {
            ...requestConfig,
            params: retryParams,
          }
        );
      } else {
        throw error;
      }
    }

    const responseData = response?.data;
    if (Array.isArray(responseData)) {
      return {
        items: responseData,
        cursor: null,
      };
    }

    return {
      items: responseData?.items || [],
      cursor: responseData?.lastEvaluatedKey || null,
    };
  },

  FETCH_WORK_INSTRUCTION_PACKAGE_USAGE_STATS: async (_, options = {}) => {
    const packageIds = Array.isArray(options?.packageIds)
      ? options.packageIds
          .map((value) => String(value || '').trim())
          .filter(Boolean)
      : [];

    if (packageIds.length < 1) {
      return [];
    }

    const response = await client.post(
      '/work-instructions-package/usage-stats',
      {
        packageIds,
        includeDeleted: options?.includeDeleted === true,
      }
    );

    return response?.data?.items || [];
  },

  FETCH_WORK_INSTRUCTION_PACKAGE_LINK_CANDIDATES: async (
    _,
    { packageId, includeUnlinked = false }
  ) => {
    const response = await client.get(
      '/work-instructions-package/link-candidates',
      { params: { packageId, includeUnlinked } }
    );

    return response?.data?.items || response?.data || [];
  },

  FETCH_LINKED_PRODUCT_TEMPLATES_BY_PACKAGE: async (_, { packageId }) => {
    const response = await client.get(
      `/work-instructions-package/${packageId}/linked-product-templates`
    );

    return response?.data?.items || response?.data || [];
  },

  FETCH_LINKED_PRODUCT_TEMPLATES_BY_PACKAGES: async (_, { packageIds }) => {
    const normalizedPackageIds = Array.isArray(packageIds)
      ? packageIds
          .map((packageId) => String(packageId || '').trim())
          .filter(Boolean)
      : [];

    if (normalizedPackageIds.length < 1) {
      return [];
    }

    const response = await client.post(
      '/work-instructions-package/linked-product-templates',
      {
        packageIds: normalizedPackageIds,
      }
    );

    return response?.data?.items || response?.data || [];
  },

  FETCH_DELETED_WORK_INSTRUCTION_PACKAGES: async () => {
    const response = await client.get('/work-instructions-package', {
      params: { deleted: true },
    });

    return (response.data || []).map(
      (wiPackage) => new WorkInstructionPackageDTO(wiPackage)
    );
  },

  RESTORE_WORK_INSTRUCTION_PACKAGE: async (_, { packageId }) => {
    const response = await client.put(
      `/work-instructions-package/${packageId}/restore`
    );

    return response?.data;
  },

  async DELETE_WORK_INSTRUCTION_PACKAGE({ commit }, { packageId }) {
    await client.delete(`/work-instructions-package/${packageId}`);

    commit(LOCAL_DELETE_WORK_INSTRUCTION_PACKAGE, { packageId });
  },

  async ADD_WORK_INSTRUCTION({ commit }, payload) {
    await client.post(
      `/work-instructions/package/${payload.packageId}`,

      payload
    );

    commit(LOCAL_ADD_WORK_INSTRUCTION, payload);
  },

  async UPDATE_WORK_INSTRUCTION({ commit }, payload) {
    await client.put(`/work-instructions/${payload._id}`, payload);

    commit(LOCAL_UPDATE_WORK_INSTRUCTION, payload);
  },

  async DELETE_WORK_INSTRUCTION({ commit }, { instructionId }) {
    await client.delete(`/work-instructions/${instructionId}`);

    commit(LOCAL_DELETE_WORK_INSTRUCTION, { instructionId });
  },

  async ADD_WORK_INSTRUCTION_STEP({ commit }, payload) {
    await client.post(
      `/work-instructions/step/${payload.instructionId}`,

      payload.step
    );

    commit(LOCAL_ADD_WORK_INSTRUCTION_STEP, {
      instructionId: payload.instructionId,

      step: payload.step,
    });
  },

  async UPDATE_WORK_INSTRUCTION_STEP({ commit }, payload) {
    await client.put(
      `/work-instructions/step/${payload.instructionId}/${payload.step._id}`,

      payload.step
    );

    commit(LOCAL_UPDATE_WORK_INSTRUCTION_STEP, {
      instructionId: payload.instructionId,

      step: payload.step,
    });
  },

  async DELETE_WORK_INSTRUCTION_STEP({ commit }, { instructionId, stepId }) {
    await client.delete(`/work-instructions/step/${instructionId}/${stepId}`);

    commit(LOCAL_DELETE_WORK_INSTRUCTION_STEP, {
      instructionId,

      stepId,
    });
  },

  FETCH_WORK_INSTRUCTIONS_FOR_PACKAGE: async ({ commit }, packageId) => {
    const response = await client.get(
      `/work-instructions/package/${packageId}`
    );

    const workInstructions = (response.data || []).map(
      (instruction) => new WorkInstructionDTO(instruction)
    );

    commit(LOCAL_SET_WORK_INSTRUCTIONS_FOR_PACKAGE, {
      packageId,
      workInstructions,
    });
  },

  FETCH_ALL_WORK_INSTRUCTIONS_FOR_JOB: async ({ commit, state }) => {
    const packages = state.workInstructionPackages;
    if (!packages || packages.length === 0) return;

    const results = await Promise.all(
      packages.map((pkg) =>
        client
          .get(`/work-instructions/package/${pkg._id}`)
          .then((res) => ({ packageId: pkg._id, workInstructions: res.data }))
      )
    );

    const allByPackage = {};
    results.forEach(({ packageId, workInstructions }) => {
      allByPackage[packageId] = workInstructions;
    });
    commit(LOCAL_SET_ALL_WORK_INSTRUCTIONS_BY_PACKAGE, allByPackage);
  },

  FETCH_WORK_INSTRUCTIONS_FOR_PACKAGE_RAW: async (_, packageId) => {
    const response = await client.get(
      `/work-instructions/package/${packageId}`
    );
    return response?.data || [];
  },

  FETCH_PRODUCT_TEMPLATE_WORK_INSTRUCTIONS: async (_, jobId) => {
    const response = await client.get(
      `/work-instructions/product-template/${jobId}`
    );
    return response?.data || [];
  },

  FETCH_LINKED_WORK_INSTRUCTION_PACKAGES_BY_TASK: async (_, taskId) => {
    const normalizedTaskId = String(taskId || '').trim();
    if (!normalizedTaskId) {
      return [];
    }

    const response = await client.get(
      `/work-instructions/linked-to-task/${normalizedTaskId}`
    );
    return response?.data || [];
  },

  UPDATE_PRODUCT_TEMPLATE_WIPS: async (_, { jobId, wipIds }) => {
    const response = await client.put(
      `/work-instructions/product-template/${jobId}/wips`,
      { wipIds }
    );
    return response?.data;
  },

  COPY_WORK_INSTRUCTIONS: async (_, payload) => {
    const response = await client.post('/work-instructions/copy', payload);
    return response?.data;
  },

  async LINK_FILES_TO_WORK_INSTRUCTION_STEP(
    { commit },

    { instructionId, files, stepId }
  ) {
    const fileIds = files.map((entry) => entry._id);

    await client.post(`/work-instructions/link-files/${instructionId}`, {
      fileIds,

      stepId,
    });

    commit(LOCAL_SET_FILES_LINKED_TO_WORK_INSTRUCTION_STEP, {
      instructionId,

      files,

      stepId,
    });
  },

  async LINK_TASKS_TO_WORK_INSTRUCTION_STEP(
    { commit },

    { instructionId, tasks, stepId }
  ) {
    const taskIds = tasks.map((entry) => entry._id);

    await client.post(`/work-instructions/link-tasks/${instructionId}`, {
      taskIds,

      stepId,
    });

    commit(LOCAL_SET_TASKS_TO_WORK_INSTRUCTION_STEP, {
      instructionId,

      tasks,

      stepId,
    });
  },

  async LINK_FILES_TO_WORK_INSTRUCTION({ commit }, { instructionId, files }) {
    const fileIds = files.map((entry) => entry._id);

    await client.post(`/work-instructions/link-files-to-wi/${instructionId}`, {
      fileIds,
    });

    commit(LOCAL_SET_FILES_LINKED_TO_WORK_INSTRUCTION, {
      instructionId,

      files,
    });
  },

  async LINK_TASKS_TO_WORK_INSTRUCTION({ commit }, { instructionId, tasks }) {
    const taskIds = tasks.map((entry) => entry._id);

    await client.post(`/work-instructions/link-tasks-to-wi/${instructionId}`, {
      taskIds,
    });

    commit(LOCAL_SET_TASKS_TO_WORK_INSTRUCTION, { instructionId, tasks });
  },

  async CHANGE_WORK_INSTRUCTION_STEPS_ORDER(
    { commit },

    { instructionId, stepId, targetStepId }
  ) {
    await client.put(`/work-instructions/steps-order/${instructionId}`, {
      stepId,

      targetStepId,
    });

    commit(LOCAL_CHANGE_WORK_INSTRUCTION_STEPS_ORDER, {
      instructionId,

      stepId,

      targetStepId,
    });
  },

  async CHANGE_WORK_INSTRUCTIONS_ORDER(
    { commit },

    { instructionId, targetInstructionId }
  ) {
    await client.put(`/work-instructions/instructions-order/${instructionId}`, {
      targetInstructionId,
    });

    commit(LOCAL_CHANGE_WORK_INSTRUCTIONS_ORDER, {
      instructionId,

      targetInstructionId,
    });
  },

  SIGN_OFF_STEP(
    { commit },

    { instruction, step, timestamp, userFullName, userId }
  ) {
    client.post(`/work-instructions/sign-off/${instruction._id}`, {
      stepId: step._id,

      timestamp,

      userFullName,

      userId,
    });

    commit(LOCAL_SIGN_OFF_STEP, {
      instruction,

      step,

      timestamp,

      userFullName,

      userId,
    });
  },

  REOPEN_STEP({ commit }, { instruction, step }) {
    client.post(`/work-instructions/reopen-step/${instruction._id}`, {
      stepId: step._id,
    });

    commit(LOCAL_REOPEN_STEP, { instruction, step });
  },

  FETCH_JOB_WORK_INSTRUCTION_STATUS: async ({ commit, state }, jobId) => {
    if (!jobId) {
      return;
    }

    if (
      state.jobWorkInstructionStatus.loading &&
      state.jobWorkInstructionStatus.jobId === jobId
    ) {
      return;
    }

    const isSameJob = state.jobWorkInstructionStatus.jobId === jobId;

    commit(LOCAL_SET_JOB_WORK_INSTRUCTION_STATUS, {
      jobId,

      loading: true,

      statusLoaded: false,

      ...(isSameJob
        ? {}
        : {
            hasInstructions: false,

            instructionCount: 0,
          }),
    });

    try {
      const { hasInstructions, instructionCount } =
        await loadJobWorkInstructionStatus(client, jobId);

      commit(LOCAL_SET_JOB_WORK_INSTRUCTION_STATUS, {
        jobId,

        hasInstructions,

        instructionCount,

        loading: false,

        statusLoaded: true,
      });
    } catch (error) {
      commit(LOCAL_SET_JOB_WORK_INSTRUCTION_STATUS, {
        jobId,

        hasInstructions: false,

        instructionCount: 0,

        loading: false,

        statusLoaded: true,
      });
    }
  },

  async ADD_COMMENT_TO_STEP(
    { dispatch, state },
    { instructionId, stepId, comment }
  ) {
    const currentPackageId = state.currentWorkInstructionPackage?._id;
    const currentPackageInstructions = currentPackageId
      ? state.workInstructionsByPackage[currentPackageId] || []
      : [];

    let instruction = currentPackageInstructions.find(
      (wi) => wi._id === instructionId
    );

    if (!instruction) {
      instruction = Object.values(state.workInstructionsByPackage)
        .flat()
        .find((wi) => wi._id === instructionId);
    }

    if (!instruction || !instruction.steps) {
      return;
    }

    const step = instruction.steps.find((entry) => entry._id === stepId);
    if (!step) {
      return;
    }

    await dispatch('UPDATE_WORK_INSTRUCTION_STEP', {
      instructionId,
      step: {
        ...step,
        comments: comment,
      },
    });
  },
};
